import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import { references, categories } from '../data/references';
import SectionHeading from '../components/SectionHeading';
import Reveal from '../components/Reveal';
import Lightbox from '../components/Lightbox';

function GallerySection() {
  const [filter, setFilter] = useState('all');
  const [lightbox, setLightbox] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const trackRef = useRef(null);

  const filtered = filter === 'all' ? references : references.filter((r) => r.category === filter);

  useEffect(() => {
    if (trackRef.current) {
      trackRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    }
    setActiveIndex(0);
  }, [filter]);

  const scrollByItem = useCallback((direction) => {
    const track = trackRef.current;
    if (!track) return;
    const item = track.querySelector('[data-carousel-item]');
    if (!item) return;
    const itemWidth = item.offsetWidth + 16;
    track.scrollBy({ left: direction * itemWidth, behavior: 'smooth' });
  }, []);

  const handleScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const item = track.querySelector('[data-carousel-item]');
    if (!item) return;
    const itemWidth = item.offsetWidth + 16;
    const index = Math.round(track.scrollLeft / itemWidth);
    setActiveIndex(Math.max(0, Math.min(index, filtered.length - 1)));
  }, [filtered.length]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      scrollByItem(1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      scrollByItem(-1);
    }
  }, [scrollByItem]);

  const openLightbox = (ref) => {
    const index = filtered.findIndex((r) => r.id === ref.id);
    setActiveIndex(index);
    setLightbox(ref);
  };

  return (
    <section id="referenzen" className="py-12 sm:py-16 md:py-28">
      <div className="container-site mx-auto">
        <Reveal>
          <SectionHeading
            eyebrow="Referenzen"
            title="Echte Einsätze, echte Baustellen."
            compact
          />
        </Reveal>

        <Reveal delay={100}>
          <div className="flex flex-wrap gap-2 mt-6">
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setFilter(cat.value)}
                aria-pressed={filter === cat.value}
                className={`px-4 py-2 min-h-[44px] rounded-full text-sm font-bold border transition-all ${
                  filter === cat.value
                    ? 'bg-signal text-ink border-signal'
                    : 'bg-white/80 border-ink/10 text-ink-soft hover:border-signal/60 hover:bg-signal/5'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </Reveal>
      </div>

      <div
        className="relative mt-8"
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="region"
        aria-roledescription="carousel"
        aria-label="Referenz-Bilder"
      >
        <div
          ref={trackRef}
          onScroll={handleScroll}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-4 px-16 sm:px-[max(1rem,calc((100vw-1180px)/2+1rem))] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {filtered.map((ref, index) => (
            <figure
              key={ref.id}
              data-carousel-item
              className="relative flex-shrink-0 w-[78vw] sm:w-[340px] lg:w-[380px] aspect-[4/3] m-0 overflow-hidden rounded-card bg-concrete shadow-[0_16px_40px_rgba(13,37,46,0.1)] cursor-pointer group/item snap-start transition-all duration-300 hover:shadow-[0_18px_42px_rgba(13,37,46,0.12)]"
              onClick={() => openLightbox(ref)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openLightbox(ref);
                }
              }}
              tabIndex={0}
              role="button"
              aria-label={`${ref.caption} – Bild ${index + 1} von ${filtered.length}`}
            >
              <img
                src={ref.src}
                srcSet={ref.srcSet}
                sizes="(max-width: 640px) 78vw, (max-width: 1024px) 340px, 380px"
                alt={ref.alt}
                width="800"
                height="600"
                loading={index === 0 ? 'eager' : 'lazy'}
                decoding="async"
                className="w-full h-full object-cover transition-all duration-300 group-hover/item:scale-[1.035] group-hover/item:saturate-[1.04]"
              />
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/0 via-transparent to-black/25 opacity-70 group-hover/item:opacity-100 transition-opacity" />
              <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none opacity-0 group-hover/item:opacity-100 transition-opacity duration-300">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 text-ink text-xs font-bold shadow-md">
                  <Maximize2 className="w-3.5 h-3.5" />
                  Vergrößern
                </span>
              </div>
              <figcaption className="absolute bottom-3 left-3 right-3 z-20 p-2.5 rounded-md bg-[rgba(21,21,21,0.78)] text-white text-xs sm:text-sm font-extrabold backdrop-blur-md">
                {ref.caption}
              </figcaption>
            </figure>
          ))}
        </div>

        <button
          onClick={() => scrollByItem(-1)}
          disabled={activeIndex === 0}
          aria-label="Vorheriges Bild"
          className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/90 border border-ink/10 shadow-[0_12px_30px_rgba(13,37,46,0.12)] flex items-center justify-center text-ink hover:bg-white hover:border-signal/40 transition-all disabled:opacity-0 disabled:pointer-events-none"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>

        <button
          onClick={() => scrollByItem(1)}
          disabled={activeIndex >= filtered.length - 1}
          aria-label="Nächstes Bild"
          className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/90 border border-ink/10 shadow-[0_12px_30px_rgba(13,37,46,0.12)] flex items-center justify-center text-ink hover:bg-white hover:border-signal/40 transition-all disabled:opacity-0 disabled:pointer-events-none"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <div className="container-site mx-auto mt-2">
        <div className="flex items-center justify-between px-1">
          <span className="text-sm font-bold text-ink-soft" aria-live="polite">
            {Math.min(activeIndex + 1, filtered.length)} von {filtered.length}
          </span>
          <div className="flex gap-1.5" aria-hidden="true">
            {filtered.map((_, index) => (
              <span
                key={index}
                className={`h-1.5 rounded-full transition-all ${
                  index === activeIndex
                    ? 'w-6 bg-signal'
                    : 'w-1.5 bg-ink/20'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {lightbox && (
        <Lightbox
          image={lightbox}
          images={filtered}
          currentIndex={filtered.findIndex((r) => r.id === lightbox.id)}
          onClose={() => setLightbox(null)}
          onNext={() => {
            const currentIndex = filtered.findIndex((r) => r.id === lightbox.id);
            const next = filtered[(currentIndex + 1) % filtered.length];
            setLightbox(next);
          }}
          onPrev={() => {
            const currentIndex = filtered.findIndex((r) => r.id === lightbox.id);
            const prev = filtered[(currentIndex - 1 + filtered.length) % filtered.length];
            setLightbox(prev);
          }}
        />
      )}
    </section>
  );
}

export default GallerySection;
